type ScrollPropMetadata = {
  previousPage: number | string | null;
};

/** The parts of an Inertia page object a history refresh is planned from. */
export type RestoredPage = {
  props: Record<string, unknown>;
  deferredProps?: Record<string, string[]>;
  initialDeferredProps?: Record<string, string[]>;
  scrollProps?: Record<string, ScrollPropMetadata>;
};

export type HistoryRefresh = {
  only: string[];
  reset: string[];
};

type Router = {
  reload: (options: HistoryRefresh) => unknown;
  on(type: 'before', callback: () => void): unknown;
  on(type: 'navigate', callback: (event: CustomEvent<{ page: RestoredPage }>) => void): unknown;
};

/**
 * Which props a refresh after a back/forward visit asks for, and which of those it replaces
 * rather than merges.
 *
 * - Deferred props stay as restored. Asking for them again would re-run whatever made them
 *   deferred — the dashboard's Quick Roll would re-roll under the user on every Back.
 * - An infinite-scroll list showing only its first page is replaced with a fresh first page.
 * - A list the user has scrolled past its first page is left alone: the refresh can only
 *   return one page, and replacing the rest with it would drop rows from under them.
 */
export const historyRefreshFor = (page: RestoredPage): HistoryRefresh => {
  const deferred = Object.values(page.initialDeferredProps ?? page.deferredProps ?? {}).flat();
  const scrolledPast: string[] = [];
  const reset: string[] = [];

  for (const [prop, scroll] of Object.entries(page.scrollProps ?? {})) {
    if (scroll.previousPage === null) {
      reset.push(prop);
    } else {
      scrolledPast.push(prop);
    }
  }

  const only = Object.keys(page.props).filter(
    prop => !deferred.includes(prop) && !scrolledPast.includes(prop)
  );

  return { only, reset };
};

/**
 * Refreshes a page from the server whenever it was restored from browser history.
 *
 * Inertia redraws a back/forward visit from the copy it saved in `history.state`, without asking
 * the server — by design, so Back is instant and lands where the user was scrolled. That copy is
 * whatever the page looked like when the user left it, so anything that changed since — a
 * generation finishing, a change made in another tab — was missing until a full reload. Every
 * restored page is re-requested straight after it is drawn: the saved copy shows at once, and the
 * current data replaces it in place.
 *
 * Three restores are covered: a popstate within the app, a document loaded as a back/forward
 * navigation (Inertia replays `history.state` over the HTML), and a page brought back whole from
 * the browser's back/forward cache. When Inertia cannot read its saved copy it fetches the page
 * itself, and the `before` of that visit stands this refresh down.
 *
 * Writes made in this tab are still handled by `freshHistoryAfterWrites`, which makes the saved
 * copies unreadable so Back fetches the page fresh. That matters for the entity studios: their
 * Details forms copy the entity once when the page mounts, so a refresh that lands after mount
 * updates the page's props but not the form. A studio form therefore still shows a change made in
 * the background only after a remount.
 */
export const installRefreshAfterHistoryVisit = (
  router: Router,
  currentPage: () => RestoredPage
): void => {
  const navigation = window.performance?.getEntriesByType('navigation')[0] as
    PerformanceNavigationTiming | undefined;

  let restoring = navigation?.type === 'back_forward';

  window.addEventListener('popstate', () => {
    restoring = true;
  });

  router.on('before', () => {
    restoring = false;
  });

  router.on('navigate', event => {
    if (!restoring) {
      return;
    }

    restoring = false;
    router.reload(historyRefreshFor(event.detail.page));
  });

  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      router.reload(historyRefreshFor(currentPage()));
    }
  });
};
